import React, { useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Button } from '../../../../common/components';
import { openModal } from '../../../../common/components/Modal/ModalRedux';
import getIncludedResource from '../../../../helpers/getIncludedResource';
import { cloneDeep, isEqual } from 'lodash';

import { isBroker } from '../../../auth/redux/authActions';

const FORM_NAME = 'IssueForm';
import { actions as productActions } from '../../redux/productActions';
import IssuePolicyForm from './components/IssuePolicyForm/IssuePolicyForm';
import { Modal } from 'react-bootstrap';
import { getMarketingFlags, getValuesForSanctions } from '../../helpers';
import changesToContent from '../../../../helpers/changesToContent';

const getLocalApplication = (application, isMta) => {
  const initialLocalApplication = cloneDeep(application);

  if (
    isMta &&
    !initialLocalApplication?.data?.attributes.metadata?.endorsements?.length
  ) {
    initialLocalApplication.data.attributes.metadata.endorsements =
      initialLocalApplication.data.attributes.metadata?.storage?.endorsements;
  }
  return initialLocalApplication;
};

const shouldShowBoltons = (premium) => {
  const availableOptions = Object.values(
    premium?.attributes?.information?.available_options ?? {},
  );
  return availableOptions.reduce((carry, option) => {
    if (option.is_bolt_on && !option.auto_include) {
      return true;
    }
    return carry;
  }, false);
};

const getPremiums = (localApplication) => {
  return getIncludedResource(
    localApplication.data,
    localApplication.included,
    'premiums',
  );
};

const getPremiumForCurrentScheme = (premiums, isGoldUpgrade, upgradeScheme, schemeId) => {
  const selectedSchemeId = isGoldUpgrade ? upgradeScheme : schemeId;
  if (!premiums || premiums.data) {
    return;
  }

  return premiums.find((premium) => {
    return premium.attributes.scheme.id === selectedSchemeId;
  });
};

const checkForFailedSanctions = (values) => {
  const travellers = values.adults.concat(values.children);
  const failingTravellers = travellers.filter(
    (traveller) => traveller?.sanctions_check?.result,
  );
  return !!failingTravellers.length;
};

const IssueButtonPolicy = ({
  application,
  block,
  definitions,
  upgradeScheme,
  schemeId,
  onUpdateApplication,
  onUpdateApplicationMetadata,
  redirectAfterSuccessfulCardPayment,
  isSaving,
  isSanctionPassed,
  isSanctionComplete,
  isMta,
  className,
  onPrepare,
  onIssuePolicy,
  onGetPremiumIndicationFromApplication,
  onGetMTAPremiumIndicationFromApplication,
  onCheckSanctions,
  premiumIndication,
  onSetPremiumIndication,
  user,
  disabled,
  productCode,
  productState,
  purchaseDeclaration,
}) => {
  const [localApplication, setLocalApplication] = useState(
    getLocalApplication(application, isMta),
  );
  const [isGoldUpgraded, setIsGoldUpgraded] = useState(false);
  const [failedSanctions, setFailedSanctions] = useState({
    adults: application?.data?.attributes?.metadata?.adults,
    children: application?.data?.attributes?.metadata?.children,
  });
  const isSmartLuggage = productCode === 'SL';

  const hasFailedSanctions = isSmartLuggage
    ? false
    : checkForFailedSanctions(failedSanctions);

  const [showModal, setShowModal] = useState(false);

  const premiums = useMemo(() => getPremiums(localApplication), [localApplication]);

  const premiumSelectedInfo = useMemo(
    () => getPremiumForCurrentScheme(premiums, isGoldUpgraded, upgradeScheme, schemeId),
    [premiums, isGoldUpgraded, upgradeScheme, schemeId],
  );

  const showBoltons = useMemo(() => shouldShowBoltons(premiumSelectedInfo), [
    premiumSelectedInfo,
  ]);

  const calculate = (newOptions) => {
    localApplication.data.attributes.metadata.options = newOptions;
    const newLocal = {
      ...localApplication,
      data: {
        ...localApplication.data,
        attributes: {
          ...localApplication.data.attributes,
          metadata: {
            ...localApplication.data.attributes.metadata,
            options: newOptions,
          },
        },
      },
    };

    if (schemeId !== upgradeScheme) {
      if (!isMta) {
        onGetPremiumIndicationFromApplication(schemeId, newLocal);
      } else {
        const policy = getIncludedResource(
          localApplication.data,
          localApplication.included,
          'policy',
        );

        onGetMTAPremiumIndicationFromApplication(policy?.data.id, newLocal, schemeId);
      }
    }
  };

  const handleOpen = () => {
    setShowModal(true);
  };

  const handleHide = () => {
    onSetPremiumIndication([]);
    setShowModal(false);
  };

  const addGoldSchemeToPolicyApplication = () => {
    localApplication.data.relationships.schemes.data.unshift({
      type: 'products/schemes',
      id: upgradeScheme,
    });

    isSaving(true);

    return Promise.resolve(
      onUpdateApplication(
        localApplication,
        null,
        localApplication.data.id,
        null,
        null,
        true,
      ),
    ).then((resp) => {
      setLocalApplication(resp);
      isSaving(false);
    });
  };

  const boltonsChanged = () => {
    const originalPremiumBoltons = application?.data?.attributes.metadata?.options;
    const premiumBoltons = localApplication?.data?.attributes?.metadata.options;
    return !isEqual(premiumBoltons, originalPremiumBoltons);
  };

  const getGoldPremium = () => {
    const selectedPremium = getPremiumForCurrentScheme(
      premiums,
      true,
      upgradeScheme,
      schemeId,
    );
    if (!selectedPremium) {
      return Promise.resolve(addGoldSchemeToPolicyApplication()).then(() => {
        return getPremiumForCurrentScheme(premiums, true, upgradeScheme, schemeId);
      });
    }
  };

  const getEndorsements = (selectedPremium) => {
    const changes = selectedPremium?.attributes?.mta?.changes;
    return changes ? [
      {
        title: 'Mid-term Adjustment',
        content: changesToContent(changes)
      }
    ]: undefined;
  }

  const getApplicationWithNewValues = (newValues, selectedPremium) => {


    const {
      adults: newAdults,
      children: newChildren,
      communications: newCommunications,
      options,
    } = newValues?.data?.attributes?.metadata ?? {};

    const travellerInfo =
      productCode === 'TRAV'
        ? {
            adults: newAdults,
            children: newChildren,
          }
        : {};
    const flags = {};
    if (newCommunications?.email) {
      flags['send_email'] = true;
    }
    if (newCommunications?.sms) {
      flags['send_sms'] = true;
    }

    const endorsements = isMta ?  {
      endorsements: getEndorsements(selectedPremium),
    }: {};

    return {
      data: {
        ...localApplication?.data,
        attributes: {
          ...localApplication?.data?.attributes,
          metadata: {
            ...localApplication?.data?.attributes?.metadata,
            ...travellerInfo,
            options: options,

            communications: {
              ...newCommunications,
            },
            sanctions_check_complete: true,
            ...endorsements,


            ...flags,
          },
        },
      },
    };
  };

  const handleUpgradeToGold = () => {
    const premium = !isGoldUpgraded
      ? getGoldPremium()
      : getPremiumForCurrentScheme(premiums, isGoldUpgraded, upgradeScheme, schemeId);
    setIsGoldUpgraded(!isGoldUpgraded);
    return premium;
  };

  const getIssueValues = (isApplicationChanged, formValues, selectedPremium) => {
    if (!isApplicationChanged) {
      const application = getApplicationWithNewValues(formValues,  selectedPremium);
      return Promise.resolve(onUpdateApplicationMetadata(application)).then(() => {
        return {
          data: {
            id: application.data.id,
            attributes: {
              metadata: {
                ...application.data.attributes.metadata,
              },
            },
            relationships: {
              premium: {
                data: {
                  id: selectedPremium.id,
                },
              },
            },
          },
          meta: {
            ...formValues.meta,
            send_sms:
              application?.data?.attributes?.metadata?.communications?.sms ?? false,
            amount: selectedPremium?.attributes?.gross,
          },
        };
      });
    }

    return Promise.resolve(
      onUpdateApplication(formValues, null, formValues.data.id, null, null, true),
    ).then((resp) => {
      const premiums = getIncludedResource(resp.data, resp.included, 'premiums');
      const premiumForIssue = premiums?.find(
        (premium) =>
          premium.attributes.scheme.id === selectedPremium?.attributes.scheme?.id,
      );

      const endorsements = isMta ?  {
        endorsements: getEndorsements(premiumForIssue),
      }: {}

      return {
        data: {
          id: resp.data.id,
          attributes: {
            metadata: {
              ...formValues.data.attributes.metadata,
             ...endorsements
            },
          },
          relationships: {
            premium: {
              data: {
                id: premiumForIssue.id,
              },
            },
          },
        },
        meta: {
          ...formValues.meta,
          amount: premiumForIssue.attributes.gross,
        },
      };
    });
  };

  const checkSanctions = (formValues, isApplicationCalculationChanges) => {
    const applicationToUse = isApplicationCalculationChanges
      ? localApplication
      : formValues;

    if (isSmartLuggage) {
      return {
        application: applicationToUse,
      };
    }

    const adults =
      applicationToUse.data?.attributes?.metadata?.adults?.map((adult) => {
        delete adult.id;
        return adult;
      }) ?? [];
    const children = applicationToUse.data?.attributes?.metadata?.children?.map(
      (child) => {
        delete child.id;
        return child;
      },
    );
    const travellers = adults.concat(children);
    const address = localApplication?.data?.attributes?.metadata?.adults?.[0]?.address;

    const toApi = getValuesForSanctions(travellers, address, schemeId);

    const formattedApplication = {
      data: {
        ...applicationToUse.data,
        attributes: {
          ...applicationToUse.data.attributes,
          metadata: {
            ...applicationToUse.data.attributes.metadata,
            adults: adults,
            children: children,
          },
        },
        relationships: {
          ...applicationToUse.data.relationships,
        },
      },
      meta: {
        ...applicationToUse.meta,
      },
    };

    return Promise.resolve(onCheckSanctions(toApi, schemeId))
      .then((response) => {
        const sanctionsChecked = response?.meta?.sanction ?? [];
        const { passed } = response.meta;
        const newAdults = adults.map((adult, index) => {
          const matchingSanction = sanctionsChecked.find(
            (sanction) => sanction.sanction_id === adult.sanction_id,
          );
          if (index === 0) {
            return {
              ...adult,
              marketing_options:
                formValues.data?.attributes?.metadata?.adults?.[0]?.marketing_options,
              metadata: formValues.data?.attributes?.metadata?.adults?.[0]?.metadata,
              sanction_result: matchingSanction.sanction,
              sanctions_check: matchingSanction.sanctions_check,
            };
          }

          return {
            ...adult,
            sanction_result: matchingSanction.sanction,
            sanctions_check: matchingSanction.sanctions_check,
          };
        });
        const newChildren = children
          ? children.map((adult) => {
              const matchingSanction = sanctionsChecked.find(
                (sanction) => sanction.sanction_id === adult.sanction_id,
              );
              return {
                ...adult,
                sanction_result: matchingSanction.sanction,
                sanctions_check: matchingSanction.sanctions_check,
              };
            })
          : [];

        const application = {
          ...formattedApplication,
          data: {
            ...formattedApplication.data,
            attributes: {
              ...formattedApplication.data.attributes,
              metadata: {
                ...formattedApplication.data.attributes.metadata,
                adults: newAdults,
                children: newChildren,
                sanctions_result: passed,
                options: formValues?.data?.attributes?.metadata?.options,
                total_sanctions: sanctionsChecked.length,
                sanctions_check_complete: true,
              },
            },
          },
          meta: {
            ...applicationToUse.meta,
          },
        };
        return {
          passed,
          application,
        };
      })
      .catch(() => {
        return {
          passed: false,
          application: formattedApplication,
        };
      });
  };

  const issue = async (formValues) => {
    isSaving(true);

    const selectedPremium = getPremiumForCurrentScheme(
      premiums,
      isGoldUpgraded,
      upgradeScheme,
      schemeId,
    );

    const isGold = selectedPremium?.attributes?.scheme.cover_level === 'Gold';
    const isApplicationChanged = isGold || boltonsChanged() || (isMta && !formValues?.data?.attributes?.metadata?.endorsements);
    const results = await checkSanctions(formValues, isApplicationChanged);
    const toSave = results.application;
    const isCard = formValues?.meta?.payment_type === 'card';
    try {
      const issueValues = await getIssueValues(
        isApplicationChanged,
        toSave,
        selectedPremium,
      );

      if (!isSmartLuggage && !results?.passed) {
        // do no issue if sanctions failed
        setFailedSanctions({
          adults: issueValues?.data?.attributes?.metadata?.adults,
          children: issueValues?.data?.attributes?.metadata?.children,
        });
        return null;
      }

      if (isCard && issueValues.meta?.amount > 0) {
        return onPrepare(
          issueValues.data.id,
          issueValues?.data?.relationships.premium.data.id,
        );
      } else {
        return onIssuePolicy(issueValues.data.id, issueValues);
      }
    } finally {
      isSaving(false);
    }
  };

  const redirectToIssued = (policyId) => {
    redirectAfterSuccessfulCardPayment(policyId);
  };

  const customer = getIncludedResource(
    localApplication.data,
    localApplication.included,
    'customer',
  );

  const premiumInfo = premiumIndication?.meta ?? premiumSelectedInfo?.attributes;

  const upgradeInfo = premiumInfo?.information?.upgrade;
  const boltonsInfo = premiumInfo?.information?.bolt_ons;

  const currentBroker = getIncludedResource(user.data, user.included, 'current_broker');

  const channel = getIncludedResource(application.data, application.included, 'channel');

  const broker = getIncludedResource(application.data, application.included, 'broker');

  const isValid = localApplication?.data?.attributes?.valid ?? false;

  const marketingFlags = isBroker()
    ? getMarketingFlags(currentBroker?.data, channel?.data)
    : getMarketingFlags(broker?.data, channel?.data);

  const salesChannel = application?.data?.attributes.metadata.sales_channel;

  const showUpgradeToGold =
    isGoldUpgraded ||
    (!!upgradeInfo?.scheme &&
      upgradeInfo?.amount?.difference > 0 &&
      upgradeInfo.cover_level === 'Gold');

  return (
    <div className="issue-btn-wrapper">
      <Button
        className={className}
        block={block}
        rightIcon="check-square-o"
        label={'Issue Policy'}
        bsStyle="primary"
        disabled={isSaving || disabled}
        isLoading={productState.isSaving}
        handleClick={handleOpen}
      />

      <Modal
        show={showModal}
        key={FORM_NAME + schemeId}
        id={FORM_NAME + schemeId}
        onHide={handleHide}
        bsSize="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Issue Policy</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {showModal && (
            <IssuePolicyForm
              addOnDefinitions={{
                travelDisputeDefinition: definitions?.travel_dispute ?? '',
                smartLuggageDefinition: definitions?.travel_dispute ?? '',
                endSupplierFailureDefinition: definitions?.smart_luggage ?? '',
              }}
              application={application}
              benefits={premiumSelectedInfo?.attributes?.benefits}
              brokerType={broker?.data?.attributes?.broker_type}
              boltonsInfo={boltonsInfo}
              calculatedPremium={
                isMta ? productState?.quotes?.[schemeId]?.meta : premiumIndication?.meta
              }
              channelType={
                channel?.data?.attributes?.channel_type === 'B2C'
                  ? 'DIR'
                  : channel?.data?.attributes?.channel_type
              }
              customer={customer}
              declarations={purchaseDeclaration}
              formName={FORM_NAME + schemeId}
              haveBoltonsChanged={boltonsChanged()}
              isGoldUpgraded={isGoldUpgraded}
              isIssuing={productState.isIssuing}
              isMarketingEnabled={marketingFlags?.marketing_enabled}
              isInsuranceMarketingOptionsEnabled={
                marketingFlags?.insurance_marketing_options_enabled
              }
              isCarInsuranceMarketingEnabled={
                marketingFlags?.car_insurance_marketing_enabled
              }
              isHomeInsuranceMarketingEnabled={
                marketingFlags?.home_insurance_marketing_enabled
              }
              isMta={isMta}
              isPremiumAdjusting={productState.isPremiumAdjusting}
              isSaving={productState.isSaving}
              isUpdatingPremiumIndication={productState.isUpdatingPremiumIndication}
              isValid={isValid}
              onCancel={handleHide}
              onCalculate={calculate}
              onIssue={issue}
              onUpgradeToGold={handleUpgradeToGold}
              onRedirectToIssued={redirectToIssued}
              productCode={productCode}
              salesChannel={salesChannel}
              selectedPremium={premiumSelectedInfo}
              selectedOptions={localApplication?.data?.attributes?.metadata?.options}
              showBoltons={!isGoldUpgraded && showBoltons}
              showUpgrades={showUpgradeToGold || showBoltons}
              showUpgradeToGold={showUpgradeToGold}
              smsNumber={customer?.data?.attributes?.phone2}
              upgradeInfo={upgradeInfo}
              isSanctionPassed={isSanctionPassed}
              isSanctionComplete={isSanctionComplete}
              hasFailedSanctions={hasFailedSanctions}
              failedSanctions={failedSanctions}
            />
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default connect(
  (state) => {
    return {
      premiumIndication: state.product.premiumIndication,
      productState: state.product,
      user: state.auth?.user,
    };
  },
  {
    onGetPremiumIndicationFromApplication:
      productActions.getPremiumIndicationFromApplication,
    onGetMTAPremiumIndicationFromApplication:
      productActions.getMTAPremiumIndicationFromApplication,
    onSetPremiumIndication: productActions.setPremiumIndication,
    onIssuePolicy: productActions.issuePolicy,
    onPrepare: productActions.prepareApplication,
    onCheckSanctions: productActions.checkMultipleSanctions,
    isSaving: productActions.isSaving,
    onUpdateApplication: productActions.updateApplication,
    onUpdateApplicationMetadata: productActions.updateApplicationMetadata,
    submitSucceeded: productActions.submitSucceeded,
    redirectAfterSuccessfulCardPayment: productActions.redirectAfterSuccessfulCardPayment,
    openModal: openModal,
  },
)(IssueButtonPolicy);
